import { useState } from 'react';
import { LoaderCircle, MapPin, Search, X } from 'lucide-react';

import { useCountryContext } from '@archipro-website/localisation';
import { cn } from '@rocco/utils/cn';
import { formatLocation } from '@rocco/utils/format-location';
import { QUESTIONNAIRE_COLOUR } from '@rocco/components/questionnaire';
import {
    FindPlaceApiProviderEnum,
    FindPlaceTypeEnum,
    type SupportedCountry,
} from 'generated/graphql';
import type { DetailedPlace } from '@modules-rocco/location/type/detailed-place';
import { usePlaceFinder } from '~/ui-rocco/hooks/location/use-place-finder';

import type { MatchAnswers } from '../answers';

/** Enough to recognise the right address without becoming a list to read. */
const SUGGESTION_LIMIT = 10;

/** Below this a keyword matches most of the country, so it is not yet a search. */
const MIN_KEYWORD_LENGTH = 3;

/**
 * The line shown for a place.
 *
 * An exact address answers the question on its own, so it wins. Anything
 * broader falls back to the shared formatter, which assembles the parts it does
 * have and drops repeats — a suburb whose city and region are both Auckland
 * would otherwise say so twice.
 */
export const describePlace = (place: DetailedPlace): string => {
    if (place.fullAddress) return place.fullAddress;

    // The shared formatter marks a place with no coordinates by appending " *".
    // That is meaningful in a filter, where it separates a real place from a
    // typed one, and reads as a typo in a sentence about someone's project.
    return formatLocation(place).replace(/\s\*$/, '');
};

/**
 * The short area label, kept alongside the exact address.
 *
 * Matching works at the region or city level, and reads this. The exact address
 * is for the people acting on the brief, not for finding suppliers who cover a
 * street.
 */
export const describeArea = (place: DetailedPlace): string =>
    place.region || place.city || place.suburb || place.district || '';

export interface LocationStepProps {
    answers: MatchAnswers;
    update: (patch: Partial<MatchAnswers>) => void;
    placeholder?: string;
}

/**
 * Where the work is, searched across addresses rather than areas alone.
 *
 * Somebody who knows their site address should be able to give it. Restricting
 * this to suburbs throws away the most specific thing they have, and the
 * structured place behind it is what a project record wants — recovering suburb,
 * city and postcode from a display string later means parsing it back apart.
 */
export const LocationStep = ({
    answers,
    update,
    placeholder = 'Suburb, city, postcode or address',
}: LocationStepProps) => {
    const { code } = useCountryContext();
    const [keyword, setKeyword] = useState('');
    const [open, setOpen] = useState(false);

    const { places, loading, handlePlaceFind, resetPlaces } = usePlaceFinder({
        apiProvider: FindPlaceApiProviderEnum.Mapbox,
        country: code as SupportedCountry,
        type: FindPlaceTypeEnum.All,
    });

    const selected = answers.location;

    const handleChange = (value: string) => {
        setKeyword(value);

        // Typing past a chosen place means it is no longer the answer, so both
        // halves go rather than leaving the label contradicting the record.
        if (selected) update({ location: null, region: '' });

        if (value.trim().length < MIN_KEYWORD_LENGTH) {
            resetPlaces();
            setOpen(false);
            return;
        }

        handlePlaceFind(value, SUGGESTION_LIMIT);
        setOpen(true);
    };

    const handleSelect = (place: DetailedPlace) => {
        // The chosen place is shown in its own right below, so the search box
        // empties rather than holding a value that is no longer being edited.
        setKeyword('');
        setOpen(false);
        resetPlaces();
        update({ location: place, region: describeArea(place) });
    };

    const handleClear = () => {
        setKeyword('');
        setOpen(false);
        resetPlaces();
        update({ location: null, region: '' });
    };

    return (
        <div className="flex flex-col gap-3">
            <div className="relative">
                <div
                    className={cn(
                        'flex h-12.5 items-center gap-2.5 rounded-3 px-3.5',
                        QUESTIONNAIRE_COLOUR.surface,
                    )}
                >
                    <Search
                        aria-hidden="true"
                        className={cn(
                            'size-3.5 shrink-0',
                            QUESTIONNAIRE_COLOUR.inkLabel,
                        )}
                    />
                    <input
                        aria-label={placeholder}
                        autoComplete="off"
                        className={cn(
                            'min-w-0 flex-1 bg-transparent font-sans text-4.75 leading-[1.3] outline-none placeholder:text-[#8c8c8c]',
                            QUESTIONNAIRE_COLOUR.ink,
                        )}
                        onChange={(event) => handleChange(event.target.value)}
                        placeholder={placeholder}
                        type="text"
                        value={keyword}
                    />
                    {loading ? (
                        <LoaderCircle
                            aria-hidden="true"
                            className="size-4 shrink-0 animate-spin text-[#8c8c8c]"
                        />
                    ) : null}
                </div>

                {open && places.length > 0 ? (
                    <ul className="absolute inset-x-0 top-full z-1 mt-1.5 max-h-90 overflow-y-auto rounded-3 border border-[#eaeaea] bg-white shadow-[0_8px_24px_rgba(0,0,0,0.10)]">
                        {places.map((place, index) => {
                            const label = describePlace(place);
                            return (
                                <li
                                    key={`${label}-${place.addressID ?? index}`}
                                >
                                    <button
                                        className={cn(
                                            'w-full px-3.5 py-3 text-left font-sans text-4 leading-[1.3] hover:bg-[#f6f6f6]',
                                            QUESTIONNAIRE_COLOUR.ink,
                                        )}
                                        onClick={() => handleSelect(place)}
                                        type="button"
                                    >
                                        {label}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                ) : null}
            </div>

            {selected ? (
                <div
                    className={cn(
                        'flex items-center gap-2.5 rounded-3 px-3.5 py-3',
                        QUESTIONNAIRE_COLOUR.surface,
                    )}
                >
                    <MapPin
                        aria-hidden="true"
                        className={cn(
                            'size-4 shrink-0',
                            QUESTIONNAIRE_COLOUR.inkSubtle,
                        )}
                    />
                    <span
                        className={cn(
                            'min-w-0 flex-1 font-sans text-4 leading-[1.3]',
                            QUESTIONNAIRE_COLOUR.ink,
                        )}
                    >
                        {describePlace(selected)}
                    </span>
                    <button
                        aria-label="Clear location"
                        className="shrink-0 rounded-[var(--radius-btn-xs)] p-1 hover:bg-black/5"
                        onClick={handleClear}
                        type="button"
                    >
                        <X aria-hidden="true" className="size-3.5" />
                    </button>
                </div>
            ) : null}
        </div>
    );
};
